import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import { useMemo } from 'react';
import SEOHead from '@/components/feature/SEOHead';

export default function ComicDetail() {
  const { id } = useParams();

  const comic = useMemo(() => {
    return comics.find((c) => c.id === Number(id));
  }, [id]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <div className="pt-20 min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-800 mb-2 font-heading">漫画未找到</h1>
          <p className="text-foreground-500 text-sm mb-6">抱歉，您要查找的漫画不存在或已下架。</p>
          <Link
            to="/category/all"
            className="px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer no-underline"
          >
            返回漫画列表
          </Link>
        </div>
      </div>
    );
  }

  const comicSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${comic.title} - 韩漫之家免费阅读`,
    'description': comic.description,
    'author': { '@type': 'Person', 'name': comic.author },
    'datePublished': comic.chapters[0]?.updateDate,
    'dateModified': comic.updateDate,
    'image': comic.cover,
    'publisher': {
      '@type': 'Organization',
      'name': '韩漫之家',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.hanmanzhijia.com' },
      { '@type': 'ListItem', 'position': 2, 'name': '分类浏览', 'item': 'https://www.hanmanzhijia.com/category/all' },
      { '@type': 'ListItem', 'position': 3, 'name': comic.category, 'item': `https://www.hanmanzhijia.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem', 'position': 4, 'name': comic.title, 'item': `https://www.hanmanzhijia.com/comic/${comic.id}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={comic.title}
        description={`${comic.title}，${comic.author}作品。${comic.description.slice(0, 120)}... 韩漫之家免费韩漫在线阅读。`}
        keywords={`${comic.title},韩漫,${comic.category}韩漫,韩国漫画,免费漫画,在线漫画,${comic.author}`}
        canonicalUrl={`/comic/${comic.id}`}
        ogImage={comic.cover}
        ogType="article"
        schema={comicSchema}
      />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors no-underline cursor-pointer">首页</Link>
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></div>
            <Link to="/category/all" className="hover:text-primary-500 transition-colors no-underline cursor-pointer">分类</Link>
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></div>
            <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-500 transition-colors no-underline cursor-pointer">{comic.category}</Link>
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></div>
            <span className="text-foreground-700 font-medium">{comic.title}</span>
          </nav>
        </div>
      </div>

      {/* Comic Detail */}
      <section className="py-8 md:py-12 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 w-full lg:w-[280px]">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩漫之家免费韩漫在线阅读`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">{comic.title}</h1>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                  comic.status === '连载中'
                    ? 'bg-accent-500 text-white'
                    : 'bg-foreground-800 text-white'
                }`}>
                  {comic.status}
                </span>
                <Link
                  to={`/category/${comic.categorySlug}`}
                  className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 whitespace-nowrap hover:bg-secondary-200 transition-colors no-underline cursor-pointer"
                >
                  {comic.category}
                </Link>
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-yellow-400 text-sm"></i></div>
                  <span className="text-sm font-semibold text-foreground-800">{comic.rating}</span>
                </div>
                <span className="text-xs text-foreground-500">{comic.views.toLocaleString()} 次阅读</span>
              </div>

              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">作者</span>
                  <span className="text-sm text-foreground-800 font-medium">{comic.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">总章节</span>
                  <span className="text-sm text-foreground-800 font-medium">{comic.chapters.length}话</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">最近更新</span>
                  <span className="text-sm text-foreground-800 font-medium">{comic.updateDate}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 border border-background-200/70 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mb-6">
                <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="flex gap-3">
                <button className="px-8 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
                  开始阅读
                </button>
                <button className="px-8 py-3 bg-background-100 text-foreground-700 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer border border-background-200/70">
                  加入收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapters */}
      <section className="py-8 md:py-10 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-lg font-bold text-foreground-950 mb-5 font-heading">章节列表</h2>
          <div className="bg-white rounded-xl overflow-hidden">
            <div className="grid grid-cols-1 divide-y divide-background-100/70">
              {comic.chapters.map((ch) => (
                <div
                  key={ch.id}
                  className="flex items-center justify-between px-5 py-4 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-foreground-800">{ch.title}</span>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{ch.updateDate}</span>
                </div>
              ))}
              {comic.chapters.length === 4 && (
                <>
                  {Array.from({ length: 50 }, (_, i) => i + 5).map((num) => (
                    <div
                      key={num}
                      className="flex items-center justify-between px-5 py-4 hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-foreground-800">第{num}话</span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">2026-{String((num % 12) + 1).padStart(2, '0')}-{String((num % 28) + 1).padStart(2, '0')}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related Comics */}
      {relatedComics.length > 0 && (
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-foreground-950 font-heading">相关推荐</h2>
              <Link
                to={`/category/${comic.categorySlug}`}
                className="text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap no-underline transition-colors cursor-pointer"
              >
                更多{comic.category}漫画
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {relatedComics.map((rc) => (
                <Link
                  key={rc.id}
                  to={`/comic/${rc.id}`}
                  className="group bg-white rounded-xl overflow-hidden hover-shimmer transition-colors cursor-pointer no-underline"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={rc.cover}
                      alt={rc.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 whitespace-nowrap overflow-hidden text-ellipsis">{rc.title}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      <div className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-yellow-400 text-[10px]"></i></div>
                      <span className="text-xs text-foreground-400">{rc.rating}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}